import React, { useRef } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { colors } from "style";
import { css } from "@emotion/react";
import Card from "components/Card";
import { PrimaryButton } from "components/Buttons";
import { InterviewTitle, InterviewSubtitle } from "components/VideoPresentation";

/**
 * IntroStep Component
 *
 * @param {string}    title
 * @param {string}    introVideoUrl
 * @param {function}  onProgress
 * @param {boolean}   canStart
 * @param {function}  onStart
 */
const IntroStep = ({ title, introVideoUrl, onProgress, canStart, onStart }) => {
  const videoRef = useRef(null);

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      onProgress(videoRef.current.currentTime || 0);
    }
  };

  return (
    <div css={styles.container}>
      <InterviewTitle titleId={title} />
      <InterviewSubtitle subtitleId="VideoPresentation.IntroStep.Subtitle" />
      <Card css={styles.card}>
        <div css={styles.badge}>
          <FormattedMessage id="VideoPresentation.IntroStep.Badge" />
        </div>
        <video
          ref={videoRef}
          src={introVideoUrl}
          controls
          onTimeUpdate={handleTimeUpdate}
          css={styles.video}
          autoPlay
        />
        <div css={styles.button_wrapper}>
          <PrimaryButton onClick={onStart} disabled={!canStart}>
            <FormattedMessage id={`VideoPresentation.IntroStep.${canStart ? "StartInterview" : "WatchMoreToUnlock"}`} />
          </PrimaryButton>
        </div>
      </Card>
    </div>
  );
};

const styles = {
  container: css`
    width: 100%;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 12px;
  `,
  card: css`
    padding: 24px;
    border-radius: 20px;
    box-shadow: 0 8px 20px rgba(17, 24, 39, 0.06);
    display: flex;
    flex-direction: column;
    gap: 20px;
  `,
  badge: css`
    padding: 4px 12px;
    align-self: flex-start;
    border-radius: 999px;
    background: ${colors.purpleRainLight3};
    border: 1px solid ${colors.purpleRainDark2};
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    color: ${colors.purpleRainBase};
    text-transform: uppercase;
  `,
  video: css`
    width: 100%;
    border-radius: 12px;
    background: ${colors.black};
    aspect-ratio: 4 / 3;
  `,
  button_wrapper: css`
    display: flex;
    justify-content: center;
    padding-top: 4px;
  `,
};

IntroStep.propTypes = {
  title: PropTypes.string.isRequired,
  introVideoUrl: PropTypes.string.isRequired,
  onProgress: PropTypes.func.isRequired,
  canStart: PropTypes.bool.isRequired,
  onStart: PropTypes.func.isRequired,
};

export default IntroStep;
